import React, { useEffect, useState, useCallback } from 'react';
import './theme/chk.css';
import { Client } from '@microsoft/microsoft-graph-client';
import { Topbar } from './components/Topbar';
import { MetricsBar } from './components/MetricsBar';
import { PipelineBoard } from './components/PipelineBoard';
import { UndoToast } from './components/UndoToast';
import { GraphService } from './services/graphService';
import { OpportunityItem, TechnicalStatus, OpportunityStatus } from './models/opportunity';
import { loginWithPopup, getCachedToken, refreshAccessToken } from './auth/authService';
import { NovaOportunidadeForm } from './components/NovaOportunidadeModal';
import { notifyNewOpportunity, replyPhaseUpdate, notifyPhaseUpdate } from './services/teamsNotify';

const SHAREPOINT_SITE_ID = process.env.REACT_APP_SHAREPOINT_SITE_ID || '';
const SHAREPOINT_LIST_ID = process.env.REACT_APP_SHAREPOINT_LIST_ID || '';
const PA_URL = process.env.REACT_APP_POWER_AUTOMATE_URL || '';

type BoardFilter = 'mine_action' | 'mine_all' | 'all' | 'execution' | 'won';

interface UndoState {
  message: string;
  onUndo: () => void;
}

export const App: React.FC = () => {
  const [opportunities, setOpportunities] = useState<OpportunityItem[]>([]);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const [loading, setLoading] = useState(false);
  const [authNeeded, setAuthNeeded] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | undefined>(undefined);
  const [boardFilter, setBoardFilter] = useState<BoardFilter>('mine_action');
  const [undo, setUndo] = useState<UndoState | null>(null);

  const getValidToken = async (): Promise<string | null> => {
    const cached = getCachedToken();
    if (cached) return cached;
    return await refreshAccessToken();
  };

  const getService = (t: string) => {
    const graphClient = Client.init({ authProvider: (done) => done(null, t) });
    return new GraphService(graphClient, SHAREPOINT_SITE_ID, SHAREPOINT_LIST_ID, PA_URL);
  };

  const loadData = async (accessToken: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getService(accessToken).getOpportunities();
      setOpportunities(data);
      setLastUpdated(new Date());
      setAuthNeeded(false);
    } catch (err: any) {
      setError('Erro ao carregar: ' + (err?.message || JSON.stringify(err)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const t = await getValidToken();
      if (t) {
        setToken(t);
        const resp = await fetch('https://graph.microsoft.com/v1.0/me', { headers: { Authorization: 'Bearer ' + t } });
        const me = await resp.json();
        setCurrentUserEmail(me.mail || me.userPrincipalName || '');
        setCurrentUserName(me.displayName || '');
        await loadData(t);
      }
    })();
  }, []);

  // Auto-refresh silencioso a cada 30s
  useEffect(() => {
    if (authNeeded) return;
    const interval = setInterval(async () => {
      const t = await getValidToken();
      if (!t) return;
      try {
        const data = await getService(t).getOpportunities();
        setOpportunities(data);
        setLastUpdated(new Date());
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [authNeeded, token]);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      const accessToken = await loginWithPopup();
      setToken(accessToken);
      const resp = await fetch('https://graph.microsoft.com/v1.0/me', { headers: { Authorization: 'Bearer ' + accessToken } });
      const me = await resp.json();
      setCurrentUserEmail(me.mail || me.userPrincipalName || '');
      setCurrentUserName(me.displayName || '');
      await loadData(accessToken);
    } catch (err: any) {
      setLoading(false);
      setError('Erro de autenticação: ' + (err?.message || JSON.stringify(err)));
    }
  };

  const withToken = async <T,>(fn: (t: string, svc: GraphService) => Promise<T>): Promise<T> => {
    let t = await getValidToken();
    if (!t) t = token;
    if (!t) throw new Error('Sem token de acesso');
    return fn(t, getService(t));
  };

  // Helper: reply no thread se tiver TeamsMessageId; senão envia mensagem nova e guarda o id
  const notifyPhaseReply = useCallback(async (
    t: string,
    svc: GraphService,
    itemId: string,
    titulo: string,
    novaFase: string,
    detalhe?: string
  ) => {
    try {
      const graphClient = Client.init({ authProvider: (done) => done(null, t) });
      const msgId = await svc.getTeamsMessageId(itemId);
      if (msgId) {
        replyPhaseUpdate(graphClient, msgId, titulo, novaFase, currentUserName, detalhe)
          .then(r => console.log('[ReplyPhase]', r))
          .catch(console.error);
      } else {
        notifyPhaseUpdate(graphClient, titulo, novaFase, currentUserName, detalhe)
          .then(async r => {
            if (r.messageId) await svc.saveTeamsMessageId(itemId, r.messageId);
          })
          .catch(console.error);
      }
    } catch (e) {
      console.warn('[notifyPhaseReply]', e);
    }
  }, [currentUserName]);

  // ── Exportar para CSV ──────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    withToken(async (_, svc) => {
      const csv = svc.exportToCsv(opportunities);
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pipeline-chk-${new Date().toISOString().substring(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(console.error);
  }, [opportunities]);

  // ── Avançar Status com Undo ────────────────────────────────────────────────
  const handleAdvanceStatus = async (id: string, newStatus: OpportunityStatus, extraFields?: Record<string, any>) => {
    const opp = opportunities.find(o => o.id === id);
    if (!opp) return;
    const previousStatus = opp.statusOportunidade;
    const previousExtra: Record<string, any> = {};
    if (opp.numeroOT) previousExtra.NumeroOT = opp.numeroOT;
    if (opp.motivoPerda) previousExtra.MotivoPerda = opp.motivoPerda;

    try {
      setError(null);
      await withToken(async (t, svc) => {
        await svc.updateOpportunityStatus(id, newStatus, extraFields, opp.faseHistorico, currentUserName);
        notifyPhaseReply(t, svc, id, opp.codigoOportunidade, newStatus);
        await loadData(t);
      });

      // Show undo toast for 8s (not for terminal states — too risky)
      const canUndo = !['10. Concluído / Ganho', '11. Perdido / Cancelado'].includes(newStatus);
      if (canUndo) {
        setUndo({
          message: `${opp.codigoOportunidade} → ${newStatus.replace(/^\d+\. /, '')}`,
          onUndo: async () => {
            setUndo(null);
            try {
              await withToken(async (t, svc) => {
                await svc.updateOpportunityStatus(id, previousStatus, previousExtra, opp.faseHistorico, currentUserName);
                await loadData(t);
              });
            } catch (e: any) {
              setError('Erro ao desfazer: ' + e?.message);
            }
          },
        });
      }
    } catch (err: any) {
      setError('Erro ao avançar: ' + (err?.message || JSON.stringify(err)));
    }
  };

  const handleUpdateExecution = async (id: string, progresso: number, entrada: string, diarioActual: string) => {
    try {
      setError(null);
      await withToken(async (t, svc) => {
        await svc.updateExecution(id, progresso, entrada, diarioActual);
        const opp = opportunities.find(o => o.id === id);
        if (opp && entrada.trim()) {
          notifyPhaseReply(t, svc, id, opp.codigoOportunidade, opp.statusOportunidade, `Progresso: ${progresso}% — ${entrada}`);
        }
        await loadData(t);
      });
    } catch (err: any) {
      setError('Erro ao gravar execução: ' + (err?.message || JSON.stringify(err)));
    }
  };

  const handleUpdateReview = async (id: string, status: TechnicalStatus, parecer: string, historicoActual: string, novoStatusOpp?: OpportunityStatus) => {
    try {
      setError(null);
      await withToken(async (t, svc) => {
        const opp = opportunities.find(o => o.id === id);
        await svc.updateTechnicalReview(
          id,
          { statusTecnico: status, parecerTecnico: parecer },
          currentUserName,
          historicoActual,
          novoStatusOpp,
          opp?.faseHistorico
        );
        if (opp) {
          const faseNotif = novoStatusOpp || opp.statusOportunidade;
          notifyPhaseReply(t, svc, id, opp.codigoOportunidade, faseNotif, parecer.trim() || undefined);
        }
        await loadData(t);
      });
    } catch (err: any) {
      setError('Erro ao gravar parecer: ' + (err?.message || JSON.stringify(err)));
    }
  };

  const handleCreateOpportunity = async (form: NovaOportunidadeForm) => {
    await withToken(async (t, svc) => {
      const graphClient = Client.init({ authProvider: (done) => done(null, t) });
      const notify = await notifyNewOpportunity(graphClient, form);
      await svc.createOpportunity(form, notify.messageId);
      await loadData(t);
    });
  };

  const handleGetNextCode = async () =>
    withToken(async (_, svc) => svc.getNextOpportunityCode()).catch(() => 'OPP-' + new Date().getFullYear() + '-001');

  const handleGetMembers = async () =>
    withToken(async (_, svc) => svc.getTeamMembers()).catch(() => []);

  const handleMetricsFilter = (filter: string) => {
    const map: Record<string, BoardFilter> = {
      technical: 'mine_action', urgent: 'mine_action',
      execution: 'execution', won: 'won',
    };
    setBoardFilter(map[filter] || 'all');
  };

  // ── Login Screen ──────────────────────────────────────────────────────────
  if (authNeeded) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 20, background: 'var(--chk-dark)' }}>
        <svg width="72" height="72" viewBox="0 0 36 36">
          <defs><clipPath id="ring2"><path d="M18 0a18 18 0 1 1 0 36A18 18 0 0 1 18 0zm0 8a10 10 0 1 0 0 20A10 10 0 0 0 18 8z"/></clipPath></defs>
          <g clipPath="url(#ring2)">
            <rect x="0" y="0" width="12" height="36" fill="#CC2936"/>
            <rect x="12" y="0" width="12" height="36" fill="#1755B8"/>
            <rect x="24" y="0" width="12" height="36" fill="#FFFFFF"/>
          </g>
        </svg>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--chk-white)' }}>CHK Pipeline</div>
          <div style={{ fontSize: 14, color: 'var(--chk-muted)', marginTop: 6 }}>DCM · DTI · Gestão de Oportunidades</div>
        </div>
        {error && (
          <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid #EF4444', borderRadius: 10, padding: '12px 18px', maxWidth: 400, color: '#F87171', fontSize: 13 }}>{error}</div>
        )}
        {loading
          ? <div style={{ color: 'var(--chk-muted)', fontSize: 14 }}>⏳ A carregar...</div>
          : <button onClick={handleLogin} style={{ background: 'linear-gradient(135deg,#CC2936,#9B1D24)', color: 'white', border: 'none', borderRadius: 10, padding: '13px 32px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              🔑 Entrar com conta CHK
            </button>
        }
        <div style={{ fontSize: 11, color: '#3A3F55' }}>CHK Lda © {new Date().getFullYear()}</div>
      </div>
    );
  }

  // ── Main App ──────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: 'var(--chk-dark)' }}>
      <Topbar userName={currentUserName} onRefresh={() => token && loadData(token)} lastUpdated={lastUpdated} />
      <MetricsBar opportunities={opportunities} onFilterClick={handleMetricsFilter} />
      {error && (
        <div style={{ background: 'rgba(239,68,68,.1)', borderBottom: '1px solid #EF4444', padding: '10px 24px', color: '#F87171', fontSize: 13 }}>{error}</div>
      )}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60, color: 'var(--chk-muted)' }}>⏳ A actualizar...</div>
      ) : (
        <PipelineBoard
          opportunities={opportunities}
          currentUserEmail={currentUserEmail}
          currentUserName={currentUserName}
          onUpdateReview={handleUpdateReview}
          onCreateOpportunity={handleCreateOpportunity}
          onAdvanceStatus={handleAdvanceStatus}
          onUpdateExecution={handleUpdateExecution}
          getMembers={handleGetMembers}
          getNextCode={handleGetNextCode}
          onExport={handleExport}
          initialFilter={boardFilter}
        />
      )}
      {undo && (
        <UndoToast
          message={`Fase avançada: ${undo.message}`}
          onUndo={undo.onUndo}
          onDismiss={() => setUndo(null)}
        />
      )}
    </div>
  );
};

export default App;
